import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  Clock,
  Download,
  FileSpreadsheet,
  Loader2,
  ReceiptIndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Expander } from "@/components/Expander";
import { EntityTrend, InvoiceTrend, Movement } from "@/components/InvoiceTrend";
import { SortHeader } from "@/components/SortHeader";
import { sortRows, useSort, asNumber } from "@/lib/sorting";
import { api, apiError, downloadFile, fmtWhen } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const RED = "hsl(0 72% 45%)";
const AMBER = "hsl(36 92% 44%)";
const GREEN = "hsl(152 55% 38%)";

export const rupees = (v) =>
  v === null || v === undefined || v === "" ? "—" : `₹${Math.round(Number(v)).toLocaleString("en-IN")}`;

const Kpi = ({ label, value, hint, colour, testId }) => (
  <div className="bg-card px-5 py-4" data-testid={testId}>
    <div className="font-data text-xl font-bold sm:text-2xl" style={colour ? { color: colour } : undefined}>
      {value}
    </div>
    <div className="font-data mt-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
      {label}
    </div>
    {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

const Pill = ({ ok, yes, no }) => (
  <span
    className="font-display border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider"
    style={{ color: ok ? GREEN : AMBER, borderColor: ok ? GREEN : AMBER }}
  >
    {ok ? yes : no}
  </span>
);

const EntitySheet = ({ entity, rows, trend, onOpenChange }) => {
  const mine = rows.filter((r) => r.entity === entity?.entity);
  return (
    <Sheet open={!!entity} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full overflow-y-auto border-l border-border bg-background sm:max-w-2xl"
        data-testid="invoice-entity-sheet"
      >
        {entity && (
          <>
            <SheetHeader className="text-left">
              <div className="font-data text-[10px] uppercase tracking-widest text-primary">
                {entity.unit || "Entity"} · invoice by invoice
              </div>
              <SheetTitle className="font-display text-2xl font-black tracking-tight">
                {entity.entity}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {entity.invoices} payment head(s) on record · {entity.raised} raised ·{" "}
                {entity.cleared_count} cleared · {entity.pending_count} pending
                {entity.avg_age ? ` · pending on average ${entity.avg_age} days` : ""}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-5 grid grid-cols-3 gap-px bg-border">
              <Kpi label="Invoiced" value={rupees(entity.invoiced)} testId="sheet-invoiced" />
              <Kpi
                label="Cleared"
                value={rupees(entity.cleared_amount)}
                colour={GREEN}
                testId="sheet-cleared"
              />
              <Kpi
                label="Pending"
                value={rupees(entity.pending_amount)}
                colour={entity.pending_amount ? RED : undefined}
                testId="sheet-pending"
              />
            </div>

            <ul className="mt-5 divide-y divide-border border border-border bg-card">              {mine.map((r, i) => (
                <li key={`${r.payment_head}-${i}`} className="p-4" data-testid={`invoice-row-${i}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{r.payment_head || "—"}</div>
                      <div className="font-data mt-1 text-[11px] text-muted-foreground">
                        {r.raised
                          ? `Invoice raised ${r.invoice_date_ddmmyyyy || "— date not stated"}`
                          : "Invoice not raised yet"}
                        {r.age_days !== null && r.age_days !== undefined
                          ? ` · ${r.age_days} days old`
                          : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-data text-sm font-bold">{rupees(r.amount)}</div>
                      <div className="mt-1 flex justify-end gap-1.5">
                        <Pill ok={r.raised} yes="Raised" no="Not raised" />
                        <Pill ok={r.cleared} yes="Cleared" no="Pending" />
                      </div>
                    </div>
                  </div>
                  {r.remarks && (
                    <p className="mt-2 border-l-2 border-border pl-3 text-[11px] text-muted-foreground">
                      {r.remarks}
                    </p>
                  )}
                </li>
              ))}
            </ul>

            <EntityTrend trend={trend} entity={entity.entity} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

const Roadmap = ({ rows }) => {
  const units = [...new Set(rows.flatMap((r) => (r.splits || []).map((s) => s.unit)))];
  const { sort, on: sortBy } = useSort("invoice-roadmap");
  const pick = (r, key) => {
    if (key === "total_value") return asNumber(r.total_value);
    if (units.includes(key)) {
      const split = (r.splits || []).find((s) => s.unit === key);
      return split ? asNumber(split.amount) : null;
    }
    return r[key];
  };
  // The totals line stays at the foot of the table, whichever way a column runs.
  const view = [
    ...sortRows(rows.filter((r) => !r.is_total), sort.key, sort.dir, pick),
    ...rows.filter((r) => r.is_total),
  ];
  return (
    <div className="overflow-x-auto" data-testid="invoice-roadmap-table">
      <table className="w-full min-w-[900px] text-left text-[11px]">
        <thead>
          <tr className="border-b border-border">
            {[
              ["activity", "Activity"],
              ["terms", "Payment terms"],
              ["milestone", "Milestone"],
            ].map(([column, label]) => (
              <th key={column} className="label-caps py-2 pr-3">
                <SortHeader label={label} column={column} sort={sort} onSort={sortBy} />
              </th>
            ))}
            <th className="label-caps py-2 pr-3 text-right">
              <SortHeader label="Value (INR)" column="total_value" sort={sort} onSort={sortBy} />
            </th>
            <th className="label-caps py-2 pr-3">
              <SortHeader label="Type" column="payment_type" sort={sort} onSort={sortBy} />
            </th>
            {units.map((u) => (
              <th key={u} className="label-caps py-2 pr-3 text-right">
                <SortHeader label={u} column={u} sort={sort} onSort={sortBy} />
              </th>
            ))}
            <th className="label-caps py-2">
              <SortHeader
                label="Tentative date"
                column="completion_on"
                sort={sort}
                onSort={sortBy}
              />
            </th>
          </tr>
        </thead>
        <tbody>
          {view.map((r) => (
            <tr
              key={r.order}
              className={`border-b border-border last:border-b-0 ${r.is_total ? "bg-muted font-bold" : ""}`}
              data-testid={`roadmap-row-${r.order}`}
            >
              <td className="py-2 pr-3">{r.is_total ? r.label : r.activity}</td>
              <td className="py-2 pr-3 text-muted-foreground">{r.terms}</td>
              <td className="py-2 pr-3">{r.milestone || r.note}</td>
              <td className="font-data py-2 pr-3 text-right">{rupees(r.total_value)}</td>
              <td className="py-2 pr-3 text-muted-foreground">{r.payment_type}</td>
              {units.map((u) => {
                const split = (r.splits || []).find((s) => s.unit === u);
                return (
                  <td key={u} className="font-data py-2 pr-3 text-right text-muted-foreground">
                    {split ? rupees(split.amount) : "—"}
                  </td>
                );
              })}
              <td className="font-data py-2 text-muted-foreground">{r.completion_on || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function InvoiceTracker() {
  const { shows } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/invoices")
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(apiError(err));
        setData({ rows: [], roadmap: [], summary: [], totals: {}, source: null });
      });
    api.get("/invoices/history").then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const take = async (id, name) => {
    setBusy(true);
    try {
      await downloadFile("/invoices/file", id ? { upload_id: id } : {}, name || "Invoice tracker.xlsx");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals || {};
  const empty = !data.rows?.length;

  return (
    <div className="p-5 sm:p-8" data-testid="invoice-tracker-page">
      <div className="flex items-center gap-2">
        <ReceiptIndianRupee className="h-4 w-4 text-primary" />
        <div className="label-caps text-primary">McKinsey payments</div>
      </div>
      <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            Invoice Tracker
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Where every McKinsey invoice stands, unit by unit — what has been raised, what Finance
            has cleared, what is still owed and how long it has been waiting. Open a unit to read
            each invoice and the remark that says exactly where it is held.
          </p>
        </div>
        {!empty && shows("invoices.download") && (
          <Button
            variant="outline"
            className="h-11 rounded-none"
            data-testid="download-invoice-file"
            disabled={busy}
            onClick={() => take("", data.source?.file_name)}
          >
            <Download className="mr-2 h-4 w-4" /> Take out the file
          </Button>
        )}
      </div>

      {empty ? (
        <div className="mt-8 border border-border bg-card p-8" data-testid="invoice-empty">
          <FileSpreadsheet className="h-6 w-6 text-muted-foreground" />
          <h2 className="mt-3 font-display text-lg font-bold">No tracker has been brought in yet</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Upload the payments workbook on <span className="font-semibold">Data files</span> — the
            same three sheets the partner keeps (Invoice wise details, Summary sheet, Payments
            roadmap). Every upload replaces what the portal holds, so the tab always shows the
            latest position. SDTD and the Pravartanam Partner Manager can bring it in.
          </p>
        </div>
      ) : (
        <>
          {data.source && (
            <p
              className="font-data mt-4 text-[11px] text-muted-foreground"
              data-testid="invoice-source"
            >
              {data.source.file_name} · brought in by {data.source.uploaded_by} ·{" "}
              {fmtWhen(data.source.uploaded_at)}
            </p>
          )}

          {data.mismatches?.length > 0 && (
            <div
              className="mt-4 border-l-2 bg-card p-4"
              style={{ borderColor: AMBER }}
              data-testid="invoice-mismatches"
            >
              <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: AMBER }}>
                <AlertTriangle className="h-4 w-4" /> The file's own Summary sheet does not match its
                rows
              </div>
              <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                {data.mismatches.map((m) => (
                  <li key={m}>• {m}</li>
                ))}
              </ul>
              <p className="mt-2 text-[11px] text-muted-foreground">
                Everything on this screen is added up from the invoice rows themselves, so the
                summary and the detail always agree.
              </p>
            </div>
          )}

          {data.trend?.movement?.uploads > 1 && (
            <div className="mt-4" data-testid="invoice-movement-strip">
              <Movement movement={data.trend.movement} />
            </div>
          )}

          <div className="mt-8 grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Total invoiced"
              value={rupees(t.invoiced)}
              hint={`${t.invoices} payment head(s) · ${t.raised} invoice(s) raised${
                t.not_raised ? ` · ${t.not_raised} yet to be raised` : ""
              }`}
              testId="kpi-invoiced"
            />
            <Kpi
              label="Payment cleared"
              value={rupees(t.cleared_amount)}
              colour={GREEN}
              hint={`${t.cleared_count} invoice(s) cleared by Finance`}
              testId="kpi-cleared"
            />
            <Kpi
              label="Payment pending"
              value={rupees(t.pending_amount)}
              colour={t.pending_amount ? RED : undefined}
              hint={`${t.pending_count} raised invoice(s) awaiting clearance`}
              testId="kpi-pending"
            />            <Kpi
              label="Oldest pending invoice"
              value={t.oldest_age ? `${t.oldest_age} days` : "—"}
              colour={t.oldest_age > 90 ? RED : AMBER}
              hint={t.avg_age ? `${t.avg_age} days on average across all pending invoices` : ""}
              testId="kpi-oldest"
            />
          </div>

          {shows("invoices.trend") && (
            <div className="mt-8">
              <InvoiceTrend trend={data.trend} />
            </div>
          )}

          <h2 className="mt-10 font-display text-lg font-bold uppercase tracking-widest">
            Unit by unit
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Sorted by what is still owed. Open a row for the invoice-by-invoice position.
          </p>

          <div
            className="mt-4 divide-y divide-border border border-border bg-card"
            data-testid="invoice-entities"
          >
            {data.summary.map((e) => (
              <button
                type="button"
                key={e.entity}
                data-testid={`invoice-entity-${e.unit || e.entity}`}
                onClick={() => setOpen(e)}
                className="flex w-full flex-wrap items-center justify-between gap-4 px-5 py-4 text-left transition-colors duration-200 hover:bg-muted"
              >
                <div className="min-w-0">
                  <div className="font-data flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    {e.unit || "—"}
                  </div>
                  <div className="mt-0.5 text-sm font-semibold">{e.entity}</div>
                  <div className="font-data mt-1 flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" style={{ color: GREEN }} />
                      {e.cleared_count} cleared
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" style={{ color: e.pending_count ? AMBER : undefined }} />
                      {e.pending_count} pending
                      {e.avg_age ? ` · ${e.avg_age} days on average` : ""}
                    </span>
                    {e.not_raised > 0 && <span>{e.not_raised} yet to be raised</span>}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="font-data text-sm font-bold">{rupees(e.invoiced)}</div>
                    <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                      invoiced
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className="font-data text-sm font-bold"
                      style={{ color: e.pending_amount ? RED : GREEN }}
                    >
                      {rupees(e.pending_amount)}
                    </div>
                    <div className="font-data text-[10px] uppercase tracking-widest text-muted-foreground">
                      pending
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </button>
            ))}
          </div>

          <div className="mt-8 space-y-4">
            {data.roadmap?.length > 0 && shows("invoices.roadmap") && (
              <Expander
                title="The contract's payment roadmap"
                note="Activity A, B and C — the milestone each payment follows, its value and the split across the units"
                testId="invoice-roadmap"
              >
                <Roadmap rows={data.roadmap} />
              </Expander>
            )}
            {history.length > 0 && shows("invoices.history") && (
              <Expander
                title="Every tracker brought in"
                note="Each upload replaced the one before it. The file itself is kept, so an earlier position can be taken out again."
                testId="invoice-history"
              >
                <ul className="divide-y divide-border">
                  {history.map((h) => (
                    <li
                      key={h.id}
                      className="flex flex-wrap items-center justify-between gap-3 py-3"
                      data-testid={`invoice-history-${h.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-xs font-semibold">
                          {h.file_name}
                          {h.active && (
                            <span className="ml-2 border border-primary px-1 text-[9px] text-primary">
                              In use
                            </span>
                          )}
                        </div>
                        <div className="font-data text-[11px] text-muted-foreground">
                          {h.uploaded_by} · {fmtWhen(h.uploaded_at)} · {h.rows} invoice(s) ·{" "}
                          {rupees(h.totals?.invoiced)} invoiced
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-none"
                        data-testid={`invoice-history-download-${h.id}`}
                        disabled={busy}
                        onClick={() => take(h.id, h.file_name)}
                      >
                        <Download className="mr-2 h-3.5 w-3.5" /> Take it out
                      </Button>
                    </li>
                  ))}
                </ul>
              </Expander>
            )}
          </div>
        </>
      )}

      <EntitySheet
        entity={open}
        rows={data.rows || []}
        trend={data.trend}
        onOpenChange={(v) => !v && setOpen(null)}
      />
    </div>
  );
}
